import { useEffect, useMemo, useState } from 'react';
import useModalState from '../../hooks/useModalState';
import {
    ComparisonStoreProvider,
    useComparisonCompanies,
    useComparisonSchemaTypes,
    useComparisonSchemaData,
    useComparisonFilterCfg,
    useComparisonTableConfig,
    useComparisonConfigActions,
} from './context';
import CompanyUtils from '../../utils/companyUtils';
import { filterChartable, processReports, prepareReportsAndDescriptors, getDescriptorLabel } from './utils';
import { Header } from 'semantic-ui-react';
import { ConfigProps, PropValues, LABEL_FORMATS } from '../../components/financialTables/configuration/constants';
import { Toggle } from '../../components/toggleGroup';
import KeyValue from '../../structures/keyValePair';
import { Chart } from '../../components/chart';
import type { ChartDataSource, ConsolidatedMode } from '../../components/chart';
import CompanySelector from './components/companySelector';
import SchemaTypeSelector from './components/schemaTypeSelector';
import ConfigForm from './components/configForm';
import StandardizedLabelsRepo from '../../utils/repository/standardizedLabelsRepo/standardizedLabelsRepo';
import { RESPONSE_STATUS } from '../../constnats/general';
import { DescriptorProps } from '../../constnats/descriptor';
import { SchemaNames } from '../../constnats/schemaConstants';
import type { StandardizedLabelType } from '../../schemas/standardizedLabel.schemas';
import './companyComparison.css';

const SCHEMA_LEGEND_ABBREVIATIONS: Record<string, string> = {
    [SchemaNames.IS]: 'IS',
    [SchemaNames.BS]: 'BS',
    [SchemaNames.CF]: 'CF',
};

function CompanyComparisonInner() {
    const companies = useComparisonCompanies();
    const selectedSchemaTypes = useComparisonSchemaTypes();
    const schemaData = useComparisonSchemaData();
    const filterCfg = useComparisonFilterCfg();
    const tableConfig = useComparisonTableConfig();
    const { setTableConfig } = useComparisonConfigActions();

    const [standardizedLabels, setStandardizedLabels] = useState<StandardizedLabelType[]>([]);
    useEffect(() => {
        StandardizedLabelsRepo.listStandardizedLabels((response) => {
            if (response.status === RESPONSE_STATUS.SUCCESS) {
                setStandardizedLabels(response.data);
            }
        });
    }, []);

    const categoryBasedMode = !!tableConfig[ConfigProps.CATEGORY_BASED_MODE];
    const labelsFormat = (tableConfig[ConfigProps.LABELS_FORMAT] as string) || '';
    const consolidatedMode: ConsolidatedMode | undefined = categoryBasedMode
        ? 'category'
        : labelsFormat === LABEL_FORMATS.STANDARDIZED
            ? 'standardized'
            : undefined;

    // Build ChartDataSource[] from schemaData, applying calculated reports, category mode, filter/sort
    const chartDataSources: ChartDataSource[] = useMemo(() => {
        const multipleSchemas = selectedSchemaTypes.length > 1;
        const sources: ChartDataSource[] = [];
        for (const data of Object.values(schemaData)) {
            if (!data.schema || data.loading || data.reports.length === 0) {
                continue;
            }

            const { dynamicFlatDescriptors, reports } =
                prepareReportsAndDescriptors(data.schema.descriptors, data.reports, categoryBasedMode);

            const chartableDescriptors = filterChartable(dynamicFlatDescriptors).map((d) => ({
                ...d,
                label: getDescriptorLabel(
                    d,
                    categoryBasedMode,
                    labelsFormat,
                    standardizedLabels.find((a) => a.id == (d as any)[DescriptorProps.STANDARDIZED_LABEL_RELATION]?.standardizedLabelId),
                ),
            }));
            const processed = processReports(reports, dynamicFlatDescriptors, filterCfg, tableConfig);
            const companyTitle = CompanyUtils.getCompanyTitle(data.company);
            const companyLegendLabel = data.company.ticker || data.company.name;
            const schemaLegendLabel = SCHEMA_LEGEND_ABBREVIATIONS[data.schema.name];
            const label = multipleSchemas ? `${companyTitle} — ${data.schema.name}` : companyTitle;

            sources.push({
                id: data.schema.id,
                label,
                companyId: data.company.id,
                companyName: companyTitle,
                companyLegendLabel,
                schemaId: data.schema.id,
                schemaName: data.schema.name,
                schemaLegendLabel,
                descriptors: chartableDescriptors,
                reports: processed as ChartDataSource['reports'],
            });
        }
        return sources;
    }, [schemaData, filterCfg, tableConfig, selectedSchemaTypes.length, standardizedLabels, categoryBasedMode, labelsFormat]);

    const downloadModal = useModalState();

    return (
        <div className="company-comparison">
            <div className="company-comparison__selectors">
                <CompanySelector />
                <SchemaTypeSelector />
            </div>

            <div className="company-comparison__config">
                <ConfigForm />
            </div>

            {companies.length === 0 ? (
                <div className="company-comparison__message">
                    Select companies and schema types to start comparing
                </div>
            ) : selectedSchemaTypes.length === 0 ? (
                <div className="company-comparison__message">
                    Select schema types to compare
                </div>
            ) : (
                <Chart.Provider dataSources={chartDataSources} descending={filterCfg[ConfigProps.REPORTS_ORDER] === PropValues.DESC} consolidatedMode={consolidatedMode} autoSelectAllPeriods>
                    <Chart.Toolbar
                        hidePeriods
                        onDownloadClick={downloadModal.openModal}
                        extraControls={
                            <div className='flex gap-20'>
                                <div className="configModule">
                                    <Header as="h5">Labels</Header>
                                    <Toggle
                                        activeValue={consolidatedMode || 'reported'}
                                        onChange={(value: string) => {
                                            setTableConfig({
                                                ...tableConfig,
                                                [ConfigProps.CATEGORY_BASED_MODE]: value === 'category',
                                                [ConfigProps.LABELS_FORMAT]: value === 'standardized'
                                                    ? LABEL_FORMATS.STANDARDIZED
                                                    : LABEL_FORMATS.DEFAULT,
                                            });
                                        }}
                                        options={[
                                            new KeyValue('category', 'Categorized'),
                                            new KeyValue('standardized', 'Standardized'),
                                            new KeyValue('reported', 'Reported As'),
                                        ]}
                                    />
                                </div>
                                <div className="configModule">
                                    <Header as="h5">View Fields</Header>
                                    <Toggle
                                        activeValue={tableConfig[ConfigProps.SHOW_IMPORTANT_ROWS_ONLY]}
                                        onChange={(value: boolean) => setTableConfig({ ...tableConfig, [ConfigProps.SHOW_IMPORTANT_ROWS_ONLY]: value })}
                                        options={[new KeyValue(false, 'All'), new KeyValue(true, 'Key Only')]}
                                    />
                                </div>
                                <div className="configModule">
                                    <Header as="h5">Missing Periods</Header>
                                    <Toggle
                                        activeValue={tableConfig[ConfigProps.SHOW_CALCULATED_STATEMENTS]}
                                        onChange={(value: boolean) => setTableConfig({ ...tableConfig, [ConfigProps.SHOW_CALCULATED_STATEMENTS]: value })}
                                        options={[new KeyValue(true, 'Calculate'), new KeyValue(false, 'Off')]}
                                    />
                                </div>
                                <div className="configModule">
                                    <Header as="h5">3D Ratios</Header>
                                    <Toggle
                                        activeValue={tableConfig[ConfigProps.SHOW_3D_RATIOS]}
                                        onChange={(value: boolean) => setTableConfig({ ...tableConfig, [ConfigProps.SHOW_3D_RATIOS]: value })}
                                        options={[new KeyValue(true, 'Calculate'), new KeyValue(false, 'Off')]}
                                    />
                                </div>
                            </div>
                        }
                    />

                    <div className="company-comparison__main">
                        <Chart.FieldSidebar />
                        <div className="company-comparison__chart-area">
                            <Chart.Heading />
                            <Chart.Renderer />
                            <Chart.Legend />
                        </div>
                    </div>
                    <Chart.DownloadModal open={downloadModal.open} onClose={downloadModal.closeModal} />
                </Chart.Provider>
            )}
        </div>
    );
}

function CompanyComparison() {
    return (
        <ComparisonStoreProvider>
            <CompanyComparisonInner />
        </ComparisonStoreProvider>
    );
}

export default CompanyComparison;
